import React from 'react';
import { utils } from '../../utils';
import BoardSprite from "./BoardSprite"
import { emit } from '../Socket/game.Emitters';

const BoardTile = ( { textureCache, ...props } ) =>
{
  return (
    <BoardSprite
      textureCache={ textureCache }
      { ...props }
    />
  );
};

class PixiTilePlacementInteractionHandler extends React.Component
{
  state = {
    currentTilePlacementHash: null,
    currentPlacementRotationIndex: 0
  };



  // Helper method to get shared render data
  getRenderData = () =>
  {
    const {
      stageRenderData,
      validTilePlacements,
    } = this.props;

    const sharedState = this.getSharedState();
    const { currentTilePlacementHash } = sharedState;

    const renderScale = stageRenderData.scale;
    const placementOptionSpriteAlpha = currentTilePlacementHash ? 0.5 : 1;

    return {
      renderScale,
      placementOptionSpriteAlpha,
      validTilePlacements,
      currentTilePlacementHash,
    };
  }

  // Renders placement options and tile preview (early DOM elements)
  renderTilePlacementBase = () =>
  {
    const {
      textureCache,
      tileFolderName,
      currentTileSprite
    } = this.props;

    const sharedState = this.getSharedState();
    const { renderScale, placementOptionSpriteAlpha, validTilePlacements, currentTilePlacementHash } = this.getRenderData();

    let placementTileOptionSprites = [];
    let tilePlacementPreview = null;

    // Generate placement option sprites
    for( let tileHash in validTilePlacements )
    {
      let placementCoords = utils.hashToCoord( tileHash );

      placementTileOptionSprites.push(
        <BoardTile
          key={ "tileOption" + placementTileOptionSprites.length }
          imageSrc="./tile_placement_option.png"
          renderScale={ renderScale }
          x={ placementCoords.x }
          y={ -1 * placementCoords.y }
          alpha={ placementOptionSpriteAlpha }
          textureCache={ textureCache }
          onClick={ () => { this.onTilePlacementClick( tileHash ); } }
        />
      );
    }

    // Generate tile placement preview if a tile is selected
    if( currentTilePlacementHash && validTilePlacements && validTilePlacements[ currentTilePlacementHash ] )
    {
      let placementCoords = utils.hashToCoord( currentTilePlacementHash );
      let currentPlacementRotationsData = validTilePlacements[ currentTilePlacementHash ];

      // Ensure we have valid rotation data and index
      if( currentPlacementRotationsData && Array.isArray( currentPlacementRotationsData ) &&
        sharedState.currentPlacementRotationIndex < currentPlacementRotationsData.length )
      {
        tilePlacementPreview = (
          <>
            <BoardTile
              imageSrc={ `./tiles/${tileFolderName}/${currentTileSprite}.png` }
              renderScale={ renderScale }
              x={ placementCoords.x }
              y={ ( -1 * placementCoords.y ) }
              tint={ 0xaaaaaa }
              angle={ currentPlacementRotationsData[ sharedState.currentPlacementRotationIndex ] * 90 }
              textureCache={ textureCache }
            />

            <BoardTile
              imageSrc={ `./tiles/${tileFolderName}/base-tile-overlay.png` }
              renderScale={ renderScale }
              x={ placementCoords.x + 0.033 }
              y={ -1 * placementCoords.y - 0.033 }
              tint={ 0xaaaaaa }
              textureCache={ textureCache }
            />
          </>
        );
      }
    }

    return (
      <>
        { placementTileOptionSprites }
        { tilePlacementPreview }
      </>
    );
  }

  // Renders interaction buttons (late DOM elements)
  renderTileInteractionControls = () =>
  {
    const { textureCache, validTilePlacements } = this.props;
    const { renderScale, currentTilePlacementHash } = this.getRenderData();

    let tileInteractionContent = [];

    if( currentTilePlacementHash && validTilePlacements && validTilePlacements[ currentTilePlacementHash ] )
    {
      let placementCoords = utils.hashToCoord( currentTilePlacementHash );
      let currentPlacementRotationsData = validTilePlacements[ currentTilePlacementHash ];

      // Ensure we have valid rotation data
      if( !currentPlacementRotationsData || !Array.isArray( currentPlacementRotationsData ) )
      {
        return tileInteractionContent;
      }

      let numValidRotations = currentPlacementRotationsData.length;

      // Add the confirm button
      tileInteractionContent.push(
        <BoardTile
          key="confirm button"
          imageSrc="./ui/button-tick.png"
          renderScale={ renderScale }
          x={ placementCoords.x + 0.7 }
          y={ ( -1 * placementCoords.y ) - 0.6 }
          textureCache={ textureCache }
          onClick={ () =>
          {
            // console.log("Confirmed placement");
            this.onTilePlacementConfirmClick();
          } }
        />
      );

      // Add the cancel button
      tileInteractionContent.push(
        <BoardTile
          key="cancel button"
          imageSrc="./ui/button-cross.png"
          renderScale={ renderScale }
          x={ placementCoords.x - 0.7 }
          y={ ( -1 * placementCoords.y ) - 0.6 }
          textureCache={ textureCache }
          onClick={ () =>
          {
            // console.log("Cancel placement");
            this.onTilePlacementCancelClick();
          } }
        />
      );

      // Add the rotate button
      if( numValidRotations > 1 )
      {
        tileInteractionContent.push(
          <BoardTile
            key="rotate button"
            imageSrc="./ui/button-rotate.png"
            renderScale={ renderScale }
            x={ placementCoords.x }
            y={ ( -1 * placementCoords.y ) - 0.6 }
            textureCache={ textureCache }
            onClick={ () =>
            {
              // console.log("Rotate placement");
              this.onTilePlacementRotateClick();
            } }
          />
        );
      }
    }

    return tileInteractionContent;
  }

  // Renders opponent tile placements (read-only, no interaction)
  renderOpponentTilePlacements = () =>
  {
    const { textureCache, validTilePlacements } = this.props;
    const { renderScale } = this.getRenderData();

    let opponentPlacementSprites = [];

    // Generate opponent placement option sprites using the opponent-specific sprite
    for( let tileHash in validTilePlacements )
    {
      let placementCoords = utils.hashToCoord( tileHash );

      opponentPlacementSprites.push(
        <BoardTile
          key={ "opponentTileOption" + opponentPlacementSprites.length }
          imageSrc="./tile_placement_option_opponents.png"
          renderScale={ renderScale }
          x={ placementCoords.x }
          y={ -1 * placementCoords.y }
          alpha={ 1 }
          textureCache={ textureCache }
        // No onClick handler - read-only
        />
      );
    }

    return opponentPlacementSprites;
  }

  // Get state (either external state or internal state)
  getSharedState = () =>
  {
    const { externalState } = this.props;
    return externalState || this.state;
  }

  // Use external handlers if provided, otherwise use internal methods
  onTilePlacementClick = ( tileHash ) =>
  {
    const { onTilePlacementClick } = this.props;
    if( onTilePlacementClick )
    {
      onTilePlacementClick( tileHash );
    } else
    {
      this.setState( { currentTilePlacementHash: tileHash, currentPlacementRotationIndex: 0 } );
    }
  }

  onTilePlacementRotateClick = () =>
  {
    const { onTilePlacementRotateClick, validTilePlacements } = this.props;
    if( onTilePlacementRotateClick )
    {
      onTilePlacementRotateClick();
    } else
    {
      const sharedState = this.getSharedState();
      const currentPlacementRotationsData = validTilePlacements && validTilePlacements[ sharedState.currentTilePlacementHash ];

      if( currentPlacementRotationsData && Array.isArray( currentPlacementRotationsData ) )
      {
        let numValidRotations = currentPlacementRotationsData.length;
        this.setState( { currentPlacementRotationIndex: ( sharedState.currentPlacementRotationIndex + 1 ) % numValidRotations } );
      }
    }
  }

  onTilePlacementCancelClick = () =>
  {
    const { onTilePlacementCancelClick } = this.props;
    if( onTilePlacementCancelClick )
    {
      onTilePlacementCancelClick();
    } else
    {
      this.setState( { currentTilePlacementHash: null } );
    }
  }

  onTilePlacementConfirmClick = () =>
  {
    const { onTilePlacementConfirmClick } = this.props;
    if( onTilePlacementConfirmClick )
    {
      onTilePlacementConfirmClick();
    } else
    {
      const sharedState = this.getSharedState();
      // Call to server to inform of placement decision
      emit.playerTilePlacement( { tileHash: sharedState.currentTilePlacementHash, rotationIndex: sharedState.currentPlacementRotationIndex } );
    }
  }

  // Default render method (maintains backward compatibility)
  render()
  {
    const { renderMode } = this.props;

    if( renderMode === "base" )
    {
      return this.renderTilePlacementBase();
    }
    else if( renderMode === "controls" )
    {
      return this.renderTileInteractionControls();
    }
    else if( renderMode === "opponent" )
    {
      return this.renderOpponentTilePlacements();
    }

    // Default: render both (backward compatibility)
    return (
      <>
        { this.renderTilePlacementBase() }
        { this.renderTileInteractionControls() }
      </>
    );
  }
}

export default PixiTilePlacementInteractionHandler;
